'use client';

import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { getMediaItemLabel, normalizePath, shouldDisplayFolder, shouldDisplayMediaFile } from '../../../../_utils';
import type { ClawFileItem, MediaFilter, MediaRow } from '../../../../types';

interface UseMediaRowsOptions {
  currentFolderItems: ClawFileItem[];
  columnCount: number;
}

export const useMediaRows = ({ currentFolderItems, columnCount }: UseMediaRowsOptions) => {
  const t = useTranslations();
  const [mediaFilter, setMediaFilter] = useState<MediaFilter>('all');

  const folderItems = useMemo(
    () => currentFolderItems.filter((item) => shouldDisplayFolder(item)),
    [currentFolderItems],
  );
  const fileItems = useMemo(
    () => currentFolderItems.filter((item) => shouldDisplayMediaFile(item)),
    [currentFolderItems],
  );
  const filteredFileItems = useMemo(() => {
    if (mediaFilter === 'all') return fileItems;
    if (mediaFilter === 'folder') return [];
    return fileItems.filter((item) => getMediaItemLabel(item) === mediaFilter);
  }, [fileItems, mediaFilter]);
  const filteredFolderItems = useMemo(() => {
    if (mediaFilter === 'all' || mediaFilter === 'folder') return folderItems;
    return [];
  }, [folderItems, mediaFilter]);

  const rows = useMemo<MediaRow[]>(() => {
    const result: MediaRow[] = [];
    const itemsPerRow = Math.max(columnCount, 1);

    if (filteredFolderItems.length > 0) {
      result.push({ key: 'folders-section', type: 'section', title: t('claw.assets.sections.folders') });
      for (let index = 0; index < filteredFolderItems.length; index += itemsPerRow) {
        const rowItems = filteredFolderItems.slice(index, index + itemsPerRow);
        result.push({
          key: `folders-${columnCount}-${rowItems.map((item) => normalizePath(item.path)).join('|')}`,
          type: 'folders',
          items: rowItems,
        });
      }
    }

    if (filteredFileItems.length > 0) {
      result.push({ key: 'files-section', type: 'section', title: t('claw.assets.sections.files') });
      for (let index = 0; index < filteredFileItems.length; index += itemsPerRow) {
        const rowItems = filteredFileItems.slice(index, index + itemsPerRow);
        result.push({
          key: `files-${columnCount}-${rowItems.map((item) => normalizePath(item.path)).join('|')}`,
          type: 'files',
          items: rowItems,
        });
      }
    }

    return result;
  }, [columnCount, filteredFileItems, filteredFolderItems]);

  return {
    mediaFilter,
    setMediaFilter,
    filteredFolderItems,
    filteredFileItems,
    rows,
  };
};
